import { useEffect, useRef, useState } from 'react';

import { Alert, Button, Input, Pagination, Row, Select, Space } from 'antd';
const { Search } = Input;

import HighlightedTable from '@Components/Tables/HighlightedTable';

import useNotification from '@/hooks/notifications';
import useFetchListData from '@/hooks/useFetchListData';
import { highlightItemPost } from '@/utils/fetchFunctions/highlight';

const searchTypes = [
	{ label: 'Search by reference ID', value: 'topicKey' },
	{ label: 'Search by keywords', value: 'search' },
];

const orderTypes = [
	{ label: 'Relevancy', value: 'relevancy:asc' },
	{ label: 'Last Update: newest', value: 'modified:desc' },
	{ label: 'Last Update: oldest', value: 'modified:asc' },
];

const timeOut = 500;

export default function HighlightedTopicsTable() {
	const [loadingChange, setLoadingChange] = useState(false);
	const [showInfoMessage, setShowInfoMessage] = useState(false);
	const fetchTimeout = useRef(null);
	const {openNotification} = useNotification();

	const associatedMediasParams =  {
		url: 'topics',
		automatic: false,
	};
	const {
		setParam,
		setParams,
		searchParams,
		data,
		getData,
		loading,
		error,
		pagination
	} = useFetchListData({params: associatedMediasParams, usePaginaton: true});

	useEffect(() => {
		setParams({sort: 'modified:desc'}, false);
	}, []);

	useEffect(() => {
		if (error) {
			openNotification({message: 'Something went wrong!', description: error.message || null, type: 'error'});
		}
	}, [error]);

	const handleSearch = () => {
		setParams({sort: 'relevancy:asc'});
	};

	async function select (id) {
		setLoadingChange(true);

		const res = await highlightItemPost({url: 'topics', id, name: 'featured'});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingChange(false);
			return;
		}

		clearTimeout(fetchTimeout.current);
		fetchTimeout.current = setTimeout(async () => {
			await getData();
			setLoadingChange(false);
			setShowInfoMessage(true);
		}, timeOut);
	}

	async function unselect (id) {
		setLoadingChange(true);
		const res = await highlightItemPost({url: 'topics', id, name: 'featured', highlighted: false});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingChange(false);
			return;
		}

		clearTimeout(fetchTimeout.current);
		fetchTimeout.current = setTimeout(async () => {
			await getData();
			setLoadingChange(false);
			setShowInfoMessage(true);
		}, timeOut);
	}

	function handlePagination (page, pageSize) {
		setParams({page, pageSize});
	}

	return (
		<Space size="large" direction='vertical' style={{width: '100%'}} >
			{showInfoMessage && (
				<Alert
					description={
						<Space size="large">
							<p>{"If you don't see the update after selecting the featured event, we recommend refreshing the page because the selected event is being indexed."}</p>
							<Button type="primary" key="back" onClick={getData}>
								Refresh
							</Button>
						</Space>
					}
					type="info"
				/>
			)}

			<Search
				addonBefore={
					<Select
						style={{width: 190}}
						options={searchTypes}
						value={searchParams.get("searchType") || 'search'}
						onChange={setParam('searchType')}
					/>
				}
				value={searchParams.get("search") || ''}
				onChange={setParam('search', false)}
				onSearch={handleSearch}
				enterButton
			/>
			<HighlightedTable
				data={data}
				loading={loading || loadingChange}
				sort={searchParams.get("sort") || 'mediaDate:desc'}
				setSort={setParam('sort')}
				getListData={getData}
				type="topic"
				select={select}
				unselect={unselect}
				updateDate
				orderTypes={orderTypes}
			/>
			<Row justify="center">
				<Pagination
						total={pagination.totalElements}
						pageSize={pagination.pageSize}
						current={pagination.page}
						showSizeChanger
						showQuickJumper
						hideOnSinglePage
						disabled={loading}
						onChange={handlePagination}
				/>
			</Row>
		</Space>
	);
}